import { Box } from "@mui/material";
import {
  type DataGridProps,
  type GridRowParams,
  type GridValidRowModel,
  DataGrid as MuiDataGrid,
} from "@mui/x-data-grid";

interface CustomDataGridProps<T extends GridValidRowModel> extends Omit<DataGridProps<T>, "sx"> {
  onRowClick?: (params: GridRowParams<T>) => void;
  containerSx?: object;
  showAlternatingRows?: boolean;
  minHeight?: string;
  maxHeight?: string;
  customSx?: object;
}

export const DataGrid = <T extends GridValidRowModel>({
  onRowClick,
  containerSx = {},
  showAlternatingRows = true,
  minHeight,
  maxHeight,
  customSx = {},
  ...dataGridProps
}: CustomDataGridProps<T>) => {
  return (
    <Box
      sx={{
        backgroundColor: "common.white",
        borderRadius: 1,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        border: "1px solid",
        borderColor: "grey.200",
        width: "100%",
        maxWidth: "100%",
        ...containerSx,
      }}
    >
      <MuiDataGrid
        {...dataGridProps}
        disableRowSelectionOnClick
        onRowClick={onRowClick}
        columnHeaderHeight={40}
        sx={{
          minHeight,
          maxHeight,
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "1px solid",
            borderBottomColor: "divider",
            minHeight: "40px !important",
            "& .MuiDataGrid-columnHeader": {
              borderRight: "1px solid",
              borderRightColor: "grey.200",
              padding: "0px 12px",
              "&:last-child": {
                borderRight: "none",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontFamily: ["Geist", "sans-serif"].join(","),
                fontWeight: 500,
                fontSize: "0.75rem",
                color: "text.secondary",
                letterSpacing: "0.025em",
                textTransform: "uppercase",
                lineHeight: 1.2,
              },
              "& .MuiDataGrid-iconButtonContainer": {
                "& .MuiDataGrid-sortIcon": {
                  fontSize: "0.875rem",
                },
              },
            },
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid",
            borderBottomColor: "background.default",
            borderRight: "1px solid",
            borderRightColor: "background.default",
            fontFamily: ["Geist", "sans-serif"].join(","),
            fontSize: "0.875rem",
            lineHeight: 1.4,
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            "&:last-child": {
              borderRight: "none",
            },
            "&:focus": {
              outline: "none",
            },
            "&:focus-within": {
              outline: "none",
            },
          },
          "& .MuiDataGrid-cell--selected": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "common.white",
            "&:hover": {
              backgroundColor: "background.default",
              transition: "background-color 0.2s ease",
            },
            ...(showAlternatingRows && {
              "&:nth-of-type(even)": {
                backgroundColor: "background.default",
                "&:hover": {
                  backgroundColor: "background.default",
                },
              },
            }),
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: "background.default",
            borderTop: "1px solid",
            borderTopColor: "divider",
            fontFamily: ["Geist", "sans-serif"].join(","),
            fontSize: "0.75rem",
            minHeight: "40px",
            "& .MuiTablePagination-root": {
              "& .MuiTablePagination-toolbar": {
                minHeight: "40px",
                "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                  fontFamily: ["Geist", "sans-serif"].join(","),
                  fontSize: "0.75rem",
                  color: "text.secondary",
                },
              },
            },
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-virtualScrollerContent": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-virtualScrollerRenderZone": {
            backgroundColor: "transparent",
          },
          ...customSx,
        }}
      />
    </Box>
  );
};
